import React, { useEffect, useId, useMemo, useState } from "react";

import { fill, getLocationStock, getVariantLabel } from "../studioEditModel";
import { Btn, Drawer, Field, Notice, Segmented, Select, TextInput, cx } from "../StudioUi";

/**
 * Maps what the person means ("add 2", "remove 1", "there are exactly 7") onto
 * the API's two modes without changing their meaning:
 *   add    → delta +q
 *   remove → delta −q
 *   set    → target q
 * Returns { payload, result, error } — error is a copy key or null.
 */
export const buildAdjustment = ({ mode, quantity, onHand, reserved }) => {
  const raw = String(quantity ?? "").trim();
  const q = /^\d+$/.test(raw) ? Number(raw) : NaN;
  if (!raw || !Number.isFinite(q) || (mode !== "set" && q < 1)) {
    return { payload: null, result: null, error: raw ? "adErrWhole" : null };
  }
  const result = mode === "add" ? onHand + q : mode === "remove" ? onHand - q : q;
  let error = null;
  if (result < 0) error = "adErrNegative";
  else if (result < reserved) error = "adErrReserved";
  else if (result === onHand) error = "adErrNoChange";
  const payload =
    mode === "set" ? { mode: "target", targetQuantity: q } : { mode: "delta", quantityDelta: mode === "add" ? q : -q };
  return { payload: error ? null : payload, result, error };
};

const AdjustStockDrawer = ({ tt, isOpen, defaultVariantId, onClose, variants, locations, onSubmit, isSaving }) => {
  const ids = useId();
  const [variantId, setVariantId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [mode, setMode] = useState("add");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("CORRECTION");
  const [note, setNote] = useState("");

  const pickLocationFor = (variant) => {
    const withStock = (variant?.inventory?.aggregateByLocation || []).find((row) => Number(row.onHand) > 0);
    if (withStock) return String(withStock.locationId);
    return locations?.[0]?.id ? String(locations[0].id) : "";
  };

  useEffect(() => {
    if (!isOpen) return;
    const preset = (variants || []).find((variant) => variant.id === defaultVariantId) || variants?.[0];
    setVariantId(preset ? String(preset.id) : "");
    setLocationId(pickLocationFor(preset));
    setMode("add");
    setQuantity("");
    setReason("CORRECTION");
    setNote("");
    // Reset only when the drawer opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const variant = useMemo(
    () => (variants || []).find((entry) => String(entry.id) === String(variantId)) || null,
    [variantId, variants]
  );
  const level = getLocationStock(variant, locationId);
  const { payload, result, error } = buildAdjustment({
    mode,
    quantity,
    onHand: level.onHand,
    reserved: level.reserved,
  });

  const handleSubmit = async () => {
    if (!payload || !variant || !locationId) return;
    await onSubmit({
      variantId: variant.id,
      locationId: Number(locationId),
      movementType: reason,
      ...payload,
      note: note.trim() || undefined,
    });
  };

  const quantityLabel = { add: tt("adQtyAdd"), remove: tt("adQtyRemove"), set: tt("adQtySet") }[mode];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={tt("adTitle")}
      description={tt("adCopy")}
      icon="sliders"
      size="md"
      closeLabel={tt("close")}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={isSaving}>
            {tt("cancel")}
          </Btn>
          <Btn variant="primary" icon="check" onClick={handleSubmit} disabled={!payload || !locationId || isSaving}>
            {isSaving ? tt("saving") : tt("adSave")}
          </Btn>
        </>
      }
    >
      <div className="pse-grid pse-grid--2">
        <Field label={tt("adVariant")} htmlFor={`${ids}-variant`}>
          <Select
            id={`${ids}-variant`}
            value={variantId}
            onChange={(value) => {
              setVariantId(value);
              setLocationId(pickLocationFor((variants || []).find((entry) => String(entry.id) === value)));
            }}
          >
            <option value="">{tt("adChooseVariant")}</option>
            {(variants || []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {getVariantLabel(entry) || fill(tt("variantFallback"), { id: entry.id })}
                {entry.sku ? ` — ${entry.sku}` : ""}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tt("adLocation")} htmlFor={`${ids}-location`}>
          <Select id={`${ids}-location`} value={locationId} onChange={setLocationId}>
            <option value="">{tt("rsChooseLocation")}</option>
            {(locations || []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="pse-summary-tiles pse-summary-tiles--3">
        <div className="pse-summary-tile">
          <span>{tt("adCurrent")}</span>
          <strong>{level.onHand}</strong>
        </div>
        <div className="pse-summary-tile pse-summary-tile--warning">
          <span>{tt("adReservedNow")}</span>
          <strong>{level.reserved}</strong>
        </div>
        <div className={cx("pse-summary-tile", "pse-summary-tile--accent", error && "is-invalid")}>
          <span>{tt("adResult")}</span>
          <strong>{result === null ? "—" : result}</strong>
        </div>
      </div>

      <section className="pse-drawer-section">
        <h3 className="pse-drawer-section__title">{tt("adWhat")}</h3>
        <Segmented
          ariaLabel={tt("adWhat")}
          className="pse-segmented--stacked"
          value={mode}
          onChange={(value) => {
            setMode(value);
            setQuantity("");
          }}
          options={[
            { value: "add", label: tt("adModeAdd"), hint: tt("adModeAddHint"), icon: "plus" },
            { value: "remove", label: tt("adModeRemove"), hint: tt("adModeRemoveHint"), icon: "minus" },
            { value: "set", label: tt("adModeSet"), hint: tt("adModeSetHint"), icon: "check" },
          ]}
        />
      </section>

      <Field
        label={quantityLabel}
        htmlFor={`${ids}-quantity`}
        error={error ? fill(tt(error), { reserved: level.reserved }) : null}
      >
        <TextInput
          id={`${ids}-quantity`}
          inputMode="numeric"
          value={quantity}
          onChange={(value) => setQuantity(value.replace(/[^0-9]/g, "").slice(0, 5))}
          className="pse-input--large"
        />
      </Field>

      <div className="pse-grid pse-grid--2">
        <Field label={tt("adReason")} htmlFor={`${ids}-reason`}>
          <Select id={`${ids}-reason`} value={reason} onChange={setReason}>
            <option value="CORRECTION">{tt("adReasonCorrection")}</option>
            <option value="MANUAL_ADJUSTMENT">{tt("adReasonManual")}</option>
          </Select>
        </Field>
        <Field label={tt("adNote")} htmlFor={`${ids}-note`} counter={`${note.length}/500`} optional optionalLabel={tt("optional")}>
          <TextInput
            id={`${ids}-note`}
            maxLength={500}
            value={note}
            placeholder={tt("adNotePlaceholder")}
            onChange={setNote}
          />
        </Field>
      </div>

      <Notice tone="info">{fill(tt("adResultNote"), { reserved: level.reserved })}</Notice>
    </Drawer>
  );
};

export default AdjustStockDrawer;
